import { useCallback, useEffect, useState } from "react";
import { AddButton, AddLink } from "../design/AddLink";
import { SCREENS, type ScreenId } from "../system/screens";
import { Link, useNavigate, useParams } from "react-router-dom";

import { may, send } from "../api/auth";
import {
  console_,
  type AvailableTransition,
  type Client,
  type ConfigPack,
  type EventTransition,
  type VerbunkEvent,
} from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import type { Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel, translateRefusal } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
// LIFTED OUT AT V11 rather than copied. `money` asks the formatter for the
// currency's exponent instead of dividing by 100, and two copies of that would
// be two chances to write the constant. See screens/format.ts.
import { typedTimeHint } from "../system/clock";
import { dayAWeekAfter } from "../system/days";
import { money, whenCompact, whenIn } from "../system/format";
import { usePartialLoad } from "./PartialLoad";
import { SearchSelect } from "./SearchSelect";
import { ChangeRequests } from "./ChangeRequests";

// ---------------------------------------------------------------------------
// EVENTS AND THE EVENT WORKSPACE — V10.
//
// V9 shipped a read-only list wedged into the configuration screen, because at
// V9 an event was three columns and a pin. It has a client, a venue, a budget
// and a state machine now, and it is the container V11–V22 hang off, so it
// gets its own file and its own workspace.
//
// THE RULES THIS FILE FOLLOWS, EACH OF WHICH IS EASY TO BREAK HERE IN
// PARTICULAR:
//
//   1. NO STATE NAME IS WRITTEN ANYWHERE BELOW. Not in a comparison, not in a
//      switch, not in a colour decision. This sentence used to NAME the two it
//      was claiming were absent, and V13's guard caught it — the seventh time
//      in this repository that a comment about a rule has tripped the rule, and
//      the reason there is no prose exemption.
//      The server sends `status_is_terminal` and `status_accepts_planning`
//      because those are the two questions the screen actually asks, and it
//      sends `available_transitions` because the alternative is the browser
//      holding a second copy of the state machine.
//   2. STATUS IS NEVER COLOUR ALONE. Every chip carries an icon and a word.
//      This is read on a cracked phone, in the rain, at 06:00 — and on a
//      projector, by somebody with a colour-vision difference.
//   3. THERE IS NO NOTES FIELD, AND ITS ABSENCE IS THE FEATURE. The event
//      workspace is the most natural place in the whole product for a free-text
//      box, which is exactly why it does not have one: a brief that is one text
//      area is where somebody records a client's request about a named worker's
//      religion. V18 owns the brief, with a structure. Absolute rule 5.
//   4. A CONTROL THE SERVER WOULD REFUSE IS NOT SHOWN, and that is courtesy
//      rather than security. The API refuses the request; the database refuses
//      the row.
// ---------------------------------------------------------------------------

// A failed read keeps its status, so a limit and a fault read differently (design/LoadFailed).
type Loaded<T> = { state: "loading" } | { state: "error"; failure: Failure } | { state: "ready"; data: T };

/**
 * How a state is DRAWN, decided from the state's own properties.
 *
 * Terminal and not plannable is finished business — neutral, because a
 * cancelled event is not an error and a delivered one is not a warning. A state
 * that accepts planning is live. Nothing here compares a name.
 */
function statusTone(event: Pick<VerbunkEvent, "status_is_terminal" | "status_accepts_planning">): Tone {
  if (event.status_is_terminal) return "neutral";
  return event.status_accepts_planning ? "live" : "attention";
}


// ===========================================================================
// The list
// ===========================================================================


export function EventList() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [loaded, setLoaded] = useState<Loaded<VerbunkEvent[]>>({ state: "loading" });
  const [statusFilter, setStatusFilter] = useState("");
  const [statuses, setStatuses] = useState<{ id: string; label: string }[]>([]);
  // S15. Search, and the next page — the list stopped at 200 with no sign it had.
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // The real total from the server (UX pass, 22 October 2026: "1-50 of 1 234", never only "Show more").
  const [total, setTotal] = useState<number | null>(null);
  // A request per keystroke would race; the last 250 ms of quiet is the search.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(typed), 250);
    return () => window.clearTimeout(t);
  }, [typed]);

  const canManage = me ? may(me, "event.manage") : false;

  const load = useCallback(
    async (cursor?: string) => {
      const { status, body } = await send<{
        events?: VerbunkEvent[];
        next_cursor?: string | null;
        total?: number;
        error?: { message?: string };
      }>(
        console_.listEvents(apiBase, {
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(search.trim() ? { q: search.trim() } : {}),
          ...(cursor ? { cursor } : {}),
        }),
      );
      if (status === 200 && body.events) {
        const page = body.events;
        setLoaded((prev) => ({
          state: "ready",
          data: cursor && prev.state === "ready" ? [...prev.data, ...page] : page,
        }));
        setNextCursor(body.next_cursor ?? null);
        setTotal(typeof body.total === "number" ? body.total : null);
      } else setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
    },
    [apiBase, statusFilter, search],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void send<{ event_statuses?: { id: string; label: string }[] }>(console_.listEventStatuses(apiBase)).then(
      ({ status, body }) => {
        if (status === 200 && body.event_statuses) setStatuses(body.event_statuses);
      },
    ).catch(() => undefined);
  }, [apiBase]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Events")}</h2>
        {canManage ? <AddLink to="/console/events/new" label={t("New event")} /> : null}
        <div className="row">
          {/* THE FILTER'S OPTIONS COME FROM THE SERVER. An empty vocabulary
              leaves one option — "All states" — which is the honest result of
              not knowing the states, rather than a list invented here. */}
          <label className="visually-hidden" htmlFor="event-search">
            {t("Search events")}
          </label>
          <input
            id="event-search"
            type="search"
            placeholder={t("Search by name or reference")}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          <label className="visually-hidden" htmlFor="event-status-filter">
            {t("Filter by state")}
          </label>
          <select
            id="event-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t("All states")}</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {translateLabel(locale, s.label)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loaded.state === "loading" ? <p className="muted">{t("Loading…")}</p> : null}

      {loaded.state === "error" ? (
        <LoadFailed failure={loaded.failure} fallback={t("We could not load the events.")} onRetry={() => void load()} />
      ) : null}

      {loaded.state === "ready" && loaded.data.length === 0 ? (
        // The real reason. The next step is the title row's plus: tour T-16 (owner) makes it the only opener to a
        // new-record form, so an empty state does not add a second, text one (event.clinic's entry 138 differs).
        <p className="muted">{statusFilter ? t("No events in that state.") : t("No events yet.")}</p>
      ) : null}

      {loaded.state === "ready" && loaded.data.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Event")}</th>
              <th scope="col">{t("Client")}</th>
              <th scope="col">{t("Starts")}</th>
              <th scope="col">{t("Venue")}</th>
              <th scope="col">{t("Crew")}</th>
              <th scope="col">{t("State")}</th>
            </tr>
          </thead>
          <tbody>
            {loaded.data.map((event) => (
              <tr key={event.id}>
                <th scope="row">
                  <Link to={`/console/events/${event.id}`}>{event.name}</Link>
                </th>
                <td>{event.client_name}</td>
                <td className="nowrap">{whenCompact(event.starts_at, orgZone)}</td>
                <td>{event.venue_name ?? "—"}</td>
                <td className="numeric">{event.seats ? `${event.seats_filled ?? 0} / ${event.seats}` : "—"}</td>
                <td>
                  <StatusChip tone={statusTone(event)} label={translateLabel(locale, event.status_label)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {loaded.state === "ready" && total !== null && loaded.data.length > 0 ? (
        <p className="muted">{t("{shown} of {total} events", { shown: loaded.data.length, total })}</p>
      ) : null}
      {nextCursor ? (
        <button type="button" className="btn btn-quiet" onClick={() => void load(nextCursor)}>
          {t("Show more")}
        </button>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Create
// ===========================================================================

/** A datetime-local value `hours` from now, for a sensible default window. */
function localAt(hours: number): string {
  const d = new Date(Date.now() + hours * 3600_000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function NewEvent() {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const partial = usePartialLoad();
  // The organisation's own zone, for the window hint below. Optional on the
  // session type, so the hint degrades to naming only the reader's own zone.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const navigate = useNavigate();
  // S15: the picker asks the server for matches; see SearchSelect.
  const [packs, setPacks] = useState<ConfigPack[]>([]);
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [startsAt, setStartsAt] = useState(localAt(24 * 30));
  const [endsAt, setEndsAt] = useState(localAt(24 * 31));
  const [venueName, setVenueName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchClients = useCallback(
    async (q: string) => {
      const res = await send<{ clients?: Client[] }>(console_.listClients(apiBase, { q, limit: 20 }));
      return (res.body.clients ?? []).map((c) => ({ id: c.id, label: c.name }));
    },
    [apiBase],
  );

  useEffect(() => {
    void send<{ config_packs?: ConfigPack[] }>(console_.listConfigPacks(apiBase)).then(({ status, body }) => {
      if (status === 200 && body.config_packs) setPacks(body.config_packs);
      else partial.note();
    }).catch(() => partial.note());
  }, [apiBase]);

  // ONLY VERSIONS THAT CAN BE PINNED ARE OFFERED, built from
  // `latest_published_version_id` — a pack with nothing published offers
  // nothing, which is the honest answer rather than an option that fails.
  const pinnable = packs.filter((p) => p.latest_published_version_id !== null);
  const ready = name.trim() !== "" && clientId !== "" && versionId !== "" && startsAt !== "" && endsAt !== "";

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { status, body } = await send<{ id?: string; error?: { message?: string } }>(
        console_.createEvent(apiBase, {
          name,
          client_account_id: clientId,
          config_pack_version_id: versionId,
          starts_at: new Date(startsAt).toISOString(),
          ends_at: new Date(endsAt).toISOString(),
          venue_name: venueName || null,
        }),
      );
      if (status !== 201 || !body.id) {
        setError(refusal(body.error) ?? t("That did not work. Please try again."));
        return;
      }
      navigate(`/console/events/${body.id}`, { replace: true });
    } catch {
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      {partial.notice}
      <div className="panel-head">
        <h2>{t("New event")}</h2>
      </div>

      <div className="form-message" role="status" aria-live="polite">
        {error ? (
          <>
            <StatusChip tone="failed" label={t("Not saved")} />
            <span>{error}</span>
          </>
        ) : null}
      </div>

      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="event-name">{t("Event name")}</label>
          <input id="event-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <SearchSelect
          id="event-client"
          label={t("Client")}
          value={clientId}
          onChange={setClientId}
          search={searchClients}
          placeholder={t("Type a client's name")}
          emptyLabel={t("Choose a client…")}
        />
        <p className="field-hint">{t("Who the work is for. It cannot be changed afterwards.")}</p>

        <div className="field">
          <label htmlFor="event-version">{t("Configuration")}</label>
          <select id="event-version" required value={versionId} onChange={(e) => setVersionId(e.target.value)}>
            <option value="">{t("Choose a published version…")}</option>
            {pinnable.map((p) => (
              <option key={p.id} value={p.latest_published_version_id as string}>
                {p.name} — v{p.latest_published_version}
              </option>
            ))}
          </select>
          <p className="field-hint">
            {t("Pinned, not referenced. What this event requires is fixed at this version even if the pack changes later.")}
          </p>
        </div>

        <div className="field">
          <label htmlFor="event-starts">{t("Starts")}</label>
          <input id="event-starts" type="datetime-local" required value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          {/* ★ WHOSE CLOCK. A datetime-local carries no zone, so this is read in
              the BROWSER's. Typing a London kickoff on a Budapest laptop books
              the crew an hour early, and nothing said so. See system/clock.ts. */}
          <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
        </div>

        <div className="field">
          <label htmlFor="event-ends">{t("Ends")}</label>
          <input id="event-ends" type="datetime-local" required value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="event-venue">{t("Venue")}</label>
          <input id="event-venue" type="text" value={venueName} onChange={(e) => setVenueName(e.target.value)} />
          <p className="field-hint">
            {t("Optional while the event is being planned. It is required before the event can be confirmed, because everything after that sends a person to it.")}
          </p>
        </div>

        <div className="row">
          <button className="btn" type="submit" disabled={busy || !ready}>
            {busy ? t("Working…") : t("Create event")}
          </button>
          <Link className="btn btn-quiet" to="/console/events">
            {t("Cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

// ===========================================================================
// The workspace
// ===========================================================================

type Workspace = {
  event: VerbunkEvent;
  timeline: EventTransition[];
  available_transitions: AvailableTransition[];
  /** S143(b). The posts this event's copy left out, from the copy's own record; empty for an event that is not a copy. */
  copy_left_out?: { sentence: string }[];
};

export function EventWorkspace() {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { id = "" } = useParams();
  const [loaded, setLoaded] = useState<Loaded<Workspace>>({ state: "loading" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [venuePostcode, setVenuePostcode] = useState("");
  const navigate = useNavigate();
  const [copyOn, setCopyOn] = useState("");
  const [copying, setCopying] = useState(false);
  const [editingVenue, setEditingVenue] = useState(false);

  async function copyEvent(startsOn: string) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const { status, body } = await send<{
        event?: { id: string };
        error?: { message?: string };
      }>(console_.copyEvent(apiBase, id, startsOn));
      if (status !== 201 || !body.event) {
        setNotice(refusal(body.error) ?? t("This event could not be copied."));
        return;
      }
      setCopyOn("");
      setCopying(false);
      navigate(`/console/events/${body.event.id}`);
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  // The provider is chosen by the SERVER's configuration, not here: whichever
  // is configured is the one asked. A picker would offer a choice a deployment
  // may not have.
  async function lookUpVenue() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const list = await send<{ providers?: Array<{ key: string }> }>(console_.geocoders(apiBase));
      const provider = list.body?.providers?.[0]?.key;
      if (!provider) {
        setNotice(t("No address lookup is configured for this deployment."));
        return;
      }
      const res = await send<{ matched?: boolean; error?: { message: string } }>(
        console_.geocodeVenue(apiBase, id, provider),
      );
      if (res.status !== 200) setNotice(refusal(res.body?.error) ?? t("That address could not be looked up."));
      else if (res.body?.matched === false) setNotice(t("No match was found for that address."));
      else await load();
    } catch {
      setNotice(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  async function useVenuePoint() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message: string } }>(console_.promoteVenuePoint(apiBase, id));
      if (res.status !== 200) setNotice(refusal(res.body?.error) ?? t("That point was not used."));
      else await load();
    } catch {
      setNotice(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  const canManage = me ? may(me, "event.manage") : false;

  const load = useCallback(async () => {
    const { status, body } = await send<Workspace & { error?: { message?: string } }>(
      console_.getEvent(apiBase, id),
    );
    if (status === 200 && body.event) {
      setLoaded({ state: "ready", data: body });
      setVenueName(body.event.venue_name ?? "");
      setVenueAddress(body.event.venue_address ?? "");
      setVenuePostcode(body.event.venue_postcode ?? "");
    } else {
      setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
    }
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(payload: Record<string, unknown>, failure: string) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.updateEvent(apiBase, id, payload),
      );
      if (status !== 200) {
        setNotice(body.error?.message ?? failure);
        return;
      }
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;

  if (loaded.state === "error") {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Event")}</h2>
        </div>
        <LoadFailed failure={loaded.failure} fallback={t("We could not load this event.")} onRetry={() => void load()} />
        <Link className="btn btn-quiet" to="/console/events">
          {t("Back to events")}
        </Link>
      </section>
    );
  }

  const { event, timeline, available_transitions: moves } = loaded.data;
  // S49, S143(b). Read with the event, so the list survives a reload.
  const leftOut = (loaded.data.copy_left_out ?? []).map((l) => l.sentence);

  return (
    <div>
      <section className="panel">
        <div className="panel-head">
          <h2>{event.name}</h2>
          <StatusChip tone={statusTone(event)} label={translateLabel(locale, event.status_label)} />
        </div>

        {notice ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone="attention" label={t("Not applied")} />
            <span>{notice}</span>
          </div>
        ) : null}

        {leftOut.length > 0 ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone="attention" label={t("Copied without some posts")} />
            <ul>
              {leftOut.map((sentence, i) => (
                <li key={i}>{sentence}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <dl className="facts">
          <div>
            <dt>{t("Client")}</dt>
            <dd>
              <Link to={`/console/clients/${event.client_account_id}`}>{event.client_name}</Link>
            </dd>
          </div>
          <div>
            <dt>{t("Window")}</dt>
            <dd>
              {whenIn(event.starts_at, orgZone)} → {whenIn(event.ends_at, orgZone)}
            </dd>
          </div>
          <div>
            <dt>{t("Venue")}</dt>
            <dd>{event.venue_name ?? t("Not set")}</dd>
          </div>
          <div>
            <dt>{t("Budget")}</dt>
            <dd>{money(event.budget_minor, event.budget_currency)}</dd>
          </div>
          <div>
            <dt>{t("Configuration")}</dt>
            <dd>
              {event.config_pack_name} v{event.config_pack_version}{" "}
              <StatusChip
                tone={event.config_pack_version_is_pinnable ? "live" : "neutral"}
                label={translateLabel(locale, event.config_pack_version_status_label)}
              />
            </dd>
          </div>
          <div>
            <dt>{t("Reference")}</dt>
            <dd>{event.reference ?? "—"}</dd>
          </div>
        </dl>
      </section>

      {/* S14. A client's requests about this event, where the coordinator already is. */}
      <ChangeRequests eventId={event.id} />

      {/* ★ ASKING AND TRUSTING ARE TWO ACTS, and the second one is stated in
          full before it is taken. 0056 makes a venue point decide whether a
          worker checks in by geofence, and promoting also wakes travel_time,
          asleep since 0045. A button that said only "use this" would be a
          silent change to how somebody starts their shift. */}
      {canManage ? (
        <section className="panel">
          <div className="panel-head">
            <h3>{t("Venue point")}</h3>
          </div>
          {event.venue_lat ? (
            <p className="muted">
              {t("This venue has a point, so workers here check in by geofence and travel time between shifts is checked.")}{" "}
              {event.geocode_precision_label
                ? t("Set from a {precision} match.", { precision: event.geocode_precision_label.toLowerCase() })
                : ""}
            </p>
          ) : !event.venue_address ? (
            <p className="muted">{t("Add a venue address above and it can be looked up.")}</p>
          ) : (
            <>
              <p className="muted">
                {t("No point yet, so workers here check in by the fallback method their policy names.")}
              </p>
              <div className="row">
                <button
                  className="btn btn-quiet"
                  type="button"
                  disabled={busy}
                  onClick={() => void lookUpVenue()}
                >
                  {t("Look up this address")}
                </button>
              </div>
            </>
          )}

          {event.geocode_precision && !event.venue_lat ? (
            event.geocode_fit_for_fence ? (
              <>
                <p className="muted">
                  Found a {String(event.geocode_precision_label).toLowerCase()} match
                  {event.geocode_provider ? ` from ${event.geocode_provider}` : ""}. Using it will:
                </p>
                <ul className="muted">
                  <li>{t("make workers at this venue check in by geofence instead of their fallback method")}</li>
                  <li>{t("start checking travel time between this shift and their others")}</li>
                </ul>
                <button className="btn btn-block" type="button" disabled={busy} onClick={() => void useVenuePoint()}>
                  {busy ? t("Working…") : t("Use this as the venue point")}
                </button>
              </>
            ) : (
              // NOT AN ERROR AND NOT A BUTTON. A street or locality answer is a
              // real result that simply cannot be a fence, and saying so is more
              // use than a disabled control with no explanation.
              <p className="muted">
                The best match was {String(event.geocode_precision_label).toLowerCase()}, which is not precise enough
                to draw a check-in fence around — it could refuse somebody standing at the gate. The address can be
                made more specific and looked up again.
              </p>
            )
          ) : null}
        </section>
      ) : null}

      {/* WHAT MAY HAPPEN NEXT, FROM THE SERVER. One button per legal move and
          no others. An event with no moves left says so in words rather than
          showing a row of disabled buttons nobody can explain. */}
      {canManage ? (
        <section className="panel">
          <div className="panel-head">
            <h3>{t("State")}</h3>
          </div>
          {moves.length === 0 ? (
            <p className="muted">
              {t("This event is {state} and its state cannot change. What was planned, and by whom, stays readable below.", {
                state: translateLabel(locale, event.status_label).toLowerCase(),
              })}
            </p>
          ) : (
            <div className="row">
              {moves.filter((move) => !move.cancels).map((move) => (
                <button
                  key={move.status}
                  className="btn btn-secondary"
                  type="button"
                  disabled={busy || (move.requires_venue && !event.venue_name)}
                  onClick={() => void patch({ status: move.status }, t("This event could not become {state}.", { state: translateLabel(locale, move.label) }))}
                >
                  {translateLabel(locale, move.action_label)}
                </button>
              ))}
            </div>
          )}
          {/* Answer 50 (0330). Cancelling cannot be undone and says the work did not happen, so
              it stands apart from the other moves and asks once more. */}
          {moves.filter((move) => move.cancels).map((move) => (
            <div className="row" key={move.status}>
              {confirmingCancel ? (
                <>
                  <p className="field-hint">
                    {t("Cancelling says this event did not happen, and it cannot be undone. Crew are not told by this button.")}
                  </p>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setConfirmingCancel(false);
                      void patch({ status: move.status }, t("This event could not become {state}.", { state: translateLabel(locale, move.label) }));
                    }}
                  >
                    {t("Yes, cancel this event")}
                  </button>
                  <button className="btn btn-quiet" type="button" onClick={() => setConfirmingCancel(false)}>
                    {t("Keep it")}
                  </button>
                </>
              ) : (
                <button className="btn btn-quiet" type="button" disabled={busy} onClick={() => setConfirmingCancel(true)}>
                  {translateLabel(locale, move.action_label)}
                </button>
              )}
            </div>
          ))}
          {moves.some((m) => m.requires_venue) && !event.venue_name ? (
            <p className="field-hint">
              {t("A venue is needed first. Everything after this state sends a person to it.")}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* S49. THE SAME EVENT ON ANOTHER DAY, without re-typing every post. The
          server says what is carried over and what is not; this only asks for
          the day, defaulting to a week on in the organisation's zone. */}
      {canManage ? (
        <section className="panel">
          <div className="panel-head">
            <h3>{t("Copy this event")}</h3>
            <AddButton label={t("Copy this event")} expanded={copying} onClick={() => setCopying(!copying)} />
          </div>
          {copying ? (
            <>
          <p className="muted">
            {t("Makes a new draft event on the day you choose, with the same client, venue, configuration and posts, each moved by the same number of days. Crew, timesheets and pay stay with this event; briefings come across unpublished.")}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void copyEvent(copyOn || dayAWeekAfter(event.starts_at, orgZone));
            }}
          >
            <div className="field">
              <label htmlFor="copy-on">{t("New start day")}</label>
              <input
                id="copy-on"
                type="date"
                required
                value={copyOn || dayAWeekAfter(event.starts_at, orgZone)}
                onChange={(e) => setCopyOn(e.target.value)}
              />
            </div>
            <div className="row">
              <button className="btn" type="submit" disabled={busy}>
                {busy ? t("Working…") : t("Copy this event to that day")}
              </button>
              <button className="btn btn-quiet" type="button" onClick={() => setCopying(false)}>
                {t("Cancel")}
              </button>
            </div>
          </form>
            </>
          ) : null}
        </section>
      ) : null}

      {canManage ? (
        <section className="panel">
          <div className="panel-head">
            <h3>{t("Venue")}</h3>
            {!editingVenue ? (
              <button className="btn btn-quiet" type="button" onClick={() => setEditingVenue(true)}>
                {t("Edit")}
              </button>
            ) : null}
          </div>
          {editingVenue ? (
            <>
          {/* LABELLED FIELDS, NOT A DESCRIPTION. A box called "venue" invites a
              venue; a box called "notes" invites whatever the person typing is
              thinking about, and on this screen that is a named worker. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void patch(
                {
                  venue_name: venueName || null,
                  venue_address: venueAddress || null,
                  venue_postcode: venuePostcode || null,
                },
                t("The venue could not be saved."),
              );
            }}
          >
            <div className="field">
              <label htmlFor="venue-name">{t("Name")}</label>
              <input id="venue-name" type="text" value={venueName} onChange={(e) => setVenueName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="venue-address">{t("Address")}</label>
              <input
                id="venue-address"
                type="text"
                value={venueAddress}
                onChange={(e) => setVenueAddress(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="venue-postcode">{t("Postcode")}</label>
              <input
                id="venue-postcode"
                type="text"
                value={venuePostcode}
                onChange={(e) => setVenuePostcode(e.target.value)}
              />
            </div>
            <div className="row">
              <button className="btn" type="submit" disabled={busy || event.status_is_terminal}>
                {busy ? t("Working…") : t("Save venue")}
              </button>
              <button className="btn btn-quiet" type="button" onClick={() => setEditingVenue(false)}>
                {t("Cancel")}
              </button>
            </div>
          </form>
            </>
          ) : (
            <p className="muted">
              {[event.venue_name, event.venue_address, event.venue_postcode].filter(Boolean).join(", ") || t("No venue recorded")}
            </p>
          )}
        </section>
      ) : null}

      <section className="panel">
        <div className="panel-head">
          <h3>{t("Timeline")}</h3>
        </div>
        {/* WHEN AND WHAT, NOT WHO. The tenant connection holds no privilege on
            the identity tables, so the server has no name to send and a column
            of user ids would be worse than no column. V22 needs an attribution
            path across the two roles; inventing one here would be inventing it
            in the wrong slice.

            THE TIMELINE IS NOT WRITTEN BY THIS PRODUCT'S APPLICATION ROLE AT
            ALL. app.event_transition has no INSERT policy and app_rw holds no
            INSERT privilege; a security-definer trigger is the only writer. So
            these rows cannot be forged, padded or pruned — including by a bug
            on this screen. */}
        {timeline.length === 0 ? (
          <p className="muted">{t("Nothing recorded yet.")}</p>
        ) : (
          <ol className="timeline">
            {timeline.map((entry) => (
              <li key={`${entry.at}-${entry.to_status}`}>
                <span className="timeline-when">{whenIn(entry.at, orgZone)}</span>
                <span className="timeline-what">{translateRefusal(locale, { message: entry.sentence, sentence_key: entry.sentence_key, params: entry.params })}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <Link className="btn btn-quiet" to="/console/events">
        {t("Back to events")}
      </Link>
    </div>
  );
}
